/**
 * Gedeelde toegankelijke accordion-init.
 *
 * Eén implementatie voor de faq- en faq-contact-blokken (voorheen twee bijna
 * identieke kopieën). Elk blok roept initAccordion() aan met z'n eigen
 * BEM-selectors/classes + het open-gedrag; de blok-specifieke styling/varianten
 * (faq licht vs faq-contact op donker) blijven volledig bij de blokken.
 *
 * Gedrag (per instance, binnen `root` gescoped):
 *  - elke vraag-knop (een native <button>, dus Enter/Space werken vanzelf)
 *    toggelt z'n item: de open-class om + aria-expanded mee;
 *  - config.singleOpen === true → max één item open: bij het openen sluiten de
 *    andere items in DEZELFDE instance (faq-contact); anders togglen items
 *    onafhankelijk (faq).
 *
 * De height-animatie + het uit-de-tab-volgorde halen van een gesloten paneel
 * zitten volledig in CSS (grid-template-rows + de visibility-transitie); deze
 * module raakt alleen de open-class + aria-expanded.
 *
 * @typedef {Object} AccordionConfig
 * @property {string} block     - Sectie-selector (bv. '.faq').
 * @property {string} item      - Item-selector (bv. '.faq__item').
 * @property {string} button    - Vraag-knop-selector (bv. '.faq__question').
 * @property {string} openClass - Open-class zonder punt (bv. 'faq__item--open').
 * @property {boolean} [singleOpen] - True = single-open, anders onafhankelijk.
 */

/**
 * Sluit één item: open-class eraf + aria-expanded false op z'n knop.
 *
 * @param {HTMLElement} item - Het accordion-item.
 * @param {AccordionConfig} config - De blok-configuratie.
 */
function closeItem(item, config) {
    item.classList.remove(config.openClass);
    const button = item.querySelector(config.button);
    if (button) {
        button.setAttribute('aria-expanded', 'false');
    }
}

/**
 * Initialiseert één accordion-instance.
 *
 * @param {HTMLElement} root - De sectie-root.
 * @param {AccordionConfig} config - De blok-configuratie.
 */
function initInstance(root, config) {
    const buttons = Array.from(root.querySelectorAll(config.button));
    if (buttons.length === 0) {
        return;
    }

    buttons.forEach((button) => {
        button.addEventListener('click', () => {
            const item = button.closest(config.item);
            if (!item) {
                return;
            }

            const willOpen = !item.classList.contains(config.openClass);

            // Single-open: eerst alle andere open items in deze instance sluiten.
            if (config.singleOpen) {
                root.querySelectorAll(`.${config.openClass}`).forEach((other) => {
                    if (other !== item) {
                        closeItem(other, config);
                    }
                });
            }

            item.classList.toggle(config.openClass, willOpen);
            button.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        });
    });
}

/**
 * Initialiseert alle accordion-instances van één blok binnen een scope.
 *
 * @param {AccordionConfig} config - De blok-configuratie.
 * @param {ParentNode} [scope] - Zoekwortel (default: document).
 */
export function initAccordion(config, scope = document) {
    scope.querySelectorAll(config.block).forEach((root) => initInstance(root, config));
}
